import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { FIXED_WING_VIEW_PRIVILEGES } from '../../core/constants/privileges.constants';
import { PermissionService } from '../../core/services/permission.service';
import { FixedWingApiService } from '../../features/fixed-wing/services/fixed-wing-api.service';
import { FIXED_WING_DEFAULT_LINE_COUNT } from '../../features/fixed-wing/utils/fixed-wing-default-lines';
import { userFacingApiError } from '../master-schedule/master-schedule-user-errors';
import { ToastActions } from '../toast/toast.actions';
import { FixedWingActions } from './fixed-wing.actions';
import { selectFixedWingDateRange, selectFixedWingState } from './fixed-wing.selectors';

const TOAST_SUMMARY = 'Fixed wing line scheduling';

@Injectable()
export class FixedWingEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(FixedWingApiService);
  private readonly store = inject(Store);
  private readonly permissionService = inject(PermissionService);

  private readonly currentDateRange$ = this.store
    .select(selectFixedWingDateRange)
    .pipe(filter((range): range is { start: string; end: string } => !!range));

  setDateRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.setDateRange),
      map(() => FixedWingActions.loadLinesForRange())
    )
  );

  loadLinesForRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.loadLinesForRange),
      switchMap(() => {
        if (!this.permissionService.hasAnyPermission(FIXED_WING_VIEW_PRIVILEGES)) {
          return of(FixedWingActions.loadLinesForRangeSuccess({ lines: [] }));
        }
        return this.currentDateRange$.pipe(
          switchMap(({ start, end }) =>
            this.api.listLines(start, end).pipe(
              map((lines) => FixedWingActions.loadLinesForRangeSuccess({ lines })),
              catchError((error) => {
                console.error(error);
                return of(
                  FixedWingActions.loadLinesForRangeFailure({ error: userFacingApiError(error) })
                );
              })
            )
          )
        );
      })
    )
  );

  loadEligibleCrewForDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.loadEligibleCrewForDate),
      mergeMap(({ date }) =>
        this.api.listEligibleCrew(date).pipe(
          map((crew) => FixedWingActions.loadEligibleCrewForDateSuccess({ date, crew })),
          catchError((error) => {
            console.error(error);
            return of(
              FixedWingActions.loadEligibleCrewForDateFailure({ error: userFacingApiError(error) })
            );
          })
        )
      )
    )
  );

  loadManageLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.loadManageLines),
      switchMap(({ date }) =>
        this.api.listLinesForDate(date).pipe(
          map((lines) => FixedWingActions.loadManageLinesSuccess({ date, lines })),
          catchError((error) => {
            console.error(error);
            const detail = userFacingApiError(error);
            return of(
              FixedWingActions.loadManageLinesFailure({ error: detail }),
              ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
            );
          })
        )
      )
    )
  );

  refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.refresh),
      map(() => FixedWingActions.loadLinesForRange())
    )
  );

  createLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.createLine),
      mergeMap(({ date, name, order, active }) =>
        this.api
          .createLine({
            date,
            name,
            order,
            isDefault: order >= 1 && order <= FIXED_WING_DEFAULT_LINE_COUNT,
            ...(active === undefined ? {} : { active }),
          })
          .pipe(
            mergeMap((line) =>
              this.store.select(selectFixedWingState).pipe(
                take(1),
                mergeMap((state) =>
                  of(
                    FixedWingActions.createLineSuccess({ line }),
                    ...(state.manageDate === line.date
                      ? [FixedWingActions.loadManageLines({ date: line.date })]
                      : []),
                    ToastActions.showToast({
                      severity: 'success',
                      summary: TOAST_SUMMARY,
                      detail: 'Line saved',
                    })
                  )
                )
              )
            ),
            catchError((error) => {
              console.error(error);
              const detail = userFacingApiError(error);
              return of(
                FixedWingActions.createLineFailure({ error: detail }),
                ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
              );
            })
          )
      )
    )
  );

  /** A rename or Active/Inactive change applies to this date only, so the board range is reloaded. */
  updateLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.updateLine),
      mergeMap(({ id, body }) =>
        this.api.updateLine(id, body).pipe(
          mergeMap((line) =>
            this.store.select(selectFixedWingState).pipe(
              take(1),
              mergeMap((state) =>
                of(
                  FixedWingActions.updateLineSuccess({ line }),
                  ...(state.manageDate === line.date
                    ? [FixedWingActions.loadManageLines({ date: line.date })]
                    : []),
                  FixedWingActions.loadLinesForRange()
                )
              )
            )
          ),
          catchError((error) => {
            console.error(error);
            const detail = userFacingApiError(error);
            return of(
              FixedWingActions.updateLineFailure({ error: detail }),
              ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
            );
          })
        )
      )
    )
  );

  /** Deleting a non-default line drops it from this date's board and from Manage Lines. */
  deleteLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.deleteLine),
      mergeMap(({ id, date }) =>
        this.api.deleteLine(id).pipe(
          mergeMap(() =>
            this.store.select(selectFixedWingState).pipe(
              take(1),
              mergeMap((state) =>
                of(
                  FixedWingActions.deleteLineSuccess({ id, date }),
                  ...(state.manageDate === date ? [FixedWingActions.loadManageLines({ date })] : []),
                  FixedWingActions.loadLinesForRange(),
                  ToastActions.showToast({
                    severity: 'success',
                    summary: TOAST_SUMMARY,
                    detail: 'Line deleted',
                  })
                )
              )
            )
          ),
          catchError((error) => {
            console.error(error);
            const detail = userFacingApiError(error);
            return of(
              FixedWingActions.deleteLineFailure({ error: detail }),
              ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
            );
          })
        )
      )
    )
  );

  assignCrew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.assignCrew),
      mergeMap(({ date, crewIds, lineId, name, order }) => {
        const body =
          lineId != null
            ? { date, crewIds, lineId }
            : { date, crewIds, name: name ?? '', order: order ?? 0 };
        return this.api.assignCrew(body).pipe(
          mergeMap(({ line, lineCreated }) =>
            this.store.select(selectFixedWingState).pipe(
              take(1),
              mergeMap((state) =>
                of(
                  FixedWingActions.assignCrewSuccess({ line, lineCreated }),
                  FixedWingActions.loadLinesForRange(),
                  ...(state.manageDate === line.date
                    ? [FixedWingActions.loadManageLines({ date: line.date })]
                    : []),
                  FixedWingActions.loadEligibleCrewForDate({ date: line.date }),
                  ToastActions.showToast({
                    severity: 'success',
                    summary: TOAST_SUMMARY,
                    detail: 'Crew assigned',
                  })
                )
              )
            )
          ),
          catchError((error) => {
            console.error(error);
            const detail = userFacingApiError(error);
            return of(
              FixedWingActions.assignCrewFailure({ error: detail }),
              ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
            );
          })
        );
      })
    )
  );

  moveCrew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.moveCrew),
      mergeMap(({ sourceLineId, crewId, targetLineId }) =>
        this.api.moveCrew(sourceLineId, crewId, targetLineId).pipe(
          mergeMap((lines) =>
            of(
              FixedWingActions.moveCrewSuccess({ lines }),
              FixedWingActions.loadLinesForRange(),
              ...(lines[0]
                ? [FixedWingActions.loadEligibleCrewForDate({ date: lines[0].date })]
                : [])
            )
          ),
          catchError((error) => {
            console.error(error);
            const detail = userFacingApiError(error);
            return of(
              FixedWingActions.moveCrewFailure({ error: detail }),
              // Resync: the optimistic move has to be rolled back to the server's state.
              FixedWingActions.loadLinesForRange(),
              ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
            );
          })
        )
      )
    )
  );

  removeCrew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.removeCrew),
      mergeMap(({ id, date, crewIds }) =>
        this.api.removeCrew(id, crewIds).pipe(
          mergeMap((removal) =>
            of(
              FixedWingActions.removeCrewSuccess({ id, date, removal }),
              FixedWingActions.loadLinesForRange(),
              FixedWingActions.loadEligibleCrewForDate({ date })
            )
          ),
          catchError((error) => {
            console.error(error);
            const detail = userFacingApiError(error);
            return of(
              FixedWingActions.removeCrewFailure({ error: detail }),
              ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
            );
          })
        )
      )
    )
  );

  applyLineAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FixedWingActions.applyLineAction),
      mergeMap(({ id, action }) =>
        this.api.applyLineAction(id, action).pipe(
          mergeMap((line) =>
            of(FixedWingActions.applyLineActionSuccess({ line }), FixedWingActions.loadLinesForRange())
          ),
          catchError((error) => {
            console.error(error);
            const detail = userFacingApiError(error);
            return of(
              FixedWingActions.applyLineActionFailure({ error: detail }),
              ToastActions.showToast({ severity: 'error', summary: TOAST_SUMMARY, detail })
            );
          })
        )
      )
    )
  );
}
